import FaviconService from 'src/services/favicon_service/favicon_service.js'
import { groupKey } from 'src/services/notification_groups/notification_groups.js'

const statusNotifications = new Set([
  'like',
  'mention',
  'status',
  'repeat',
  'pleroma:emoji_reaction',
  'poll',
])

let cachedBadgeUrl = null

export const isStatusNotification = (type) => statusNotifications.has(type)

export const prepareNotificationObject = (notification, i18n) => {
  if (cachedBadgeUrl === null) {
    const favicons = FaviconService.getOriginalFavicons()
    const favicon = favicons[favicons.length - 1]
    if (!favicon) {
      cachedBadgeUrl = 'about:blank'
    } else {
      cachedBadgeUrl = favicon.favimg.src
    }
  }

  const status = notification.status
  const title = notification.from_profile.name
  // pop-ups of one group share a tag, so a new one replaces the one shown
  // (see mergePopup); data says who did what
  const notifObj = {
    tag: groupKey(notification) ?? notification.id,
    type: notification.type,
    badge: cachedBadgeUrl,
    data: {
      ids: [notification.id],
      people: [title],
      kinds: [notification.type],
    },
  }
  notifObj.title = title
  notifObj.icon = notification.from_profile.profile_image_url
  let i18nString
  switch (notification.type) {
    case 'like':
      i18nString = 'favorited_you'
      break
    case 'status':
      i18nString = 'subscribed_status'
      break
    case 'repeat':
      i18nString = 'repeated_you'
      break
    case 'follow':
      i18nString = 'followed_you'
      break
    case 'move':
      i18nString = 'migrated_to'
      break
    case 'follow_request':
      i18nString = 'follow_request'
      break
    case 'pleroma:report':
      i18nString = 'submitted_report'
      break
    case 'poll':
      i18nString = 'poll_ended'
      break
  }

  if (notification.type === 'pleroma:emoji_reaction') {
    notifObj.body = i18n.t('notifications.reacted_with', [notification.emoji])
  } else if (i18nString) {
    notifObj.body = i18n.t('notifications.' + i18nString)
  } else if (isStatusNotification(notification.type)) {
    notifObj.body = notification.status.text
  }

  // Shows first attached non-nsfw image, if any. Should add configuration for this somehow...
  if (
    !status?.nsfw &&
    status?.attachments?.[0]?.mimetype.startsWith('image/')
  ) {
    notifObj.image = status.attachments[0].url
  }

  return notifObj
}

const POPUP_VERBS = {
  like: 'notifications.favorited_you',
  repeat: 'notifications.repeated_you',
  follow: 'notifications.followed_you',
  'pleroma:emoji_reaction': 'notifications.group_reacted',
}

const popupNames = (people, i18n) => {
  const [first, second] = people
  if (people.length === 1) return first
  if (people.length === 2) {
    return i18n.t('notifications.group_two', { first, second })
  }
  const count = people.length - 2
  return i18n.t('notifications.group_more', { first, second, count }, count)
}

// A pop-up for a notification whose group already has one on screen
// (shown: that pop-up's data): one pop-up for all of them, "kaia and Bob
// favorited your status". It rings again, as a new one would, unless the
// notification is one it already shows.
export const mergePopup = (shown, next, i18n) => {
  if (!shown?.ids) return next
  const unique = (a, b) => [...new Set([...a, ...b])]
  const data = {
    ids: unique(next.data.ids, shown.ids),
    people: unique(next.data.people, shown.people),
    kinds: unique(next.data.kinds, shown.kinds),
  }
  const verb =
    data.kinds.length > 1
      ? 'notifications.group_interacted'
      : POPUP_VERBS[data.kinds[0]]
  return {
    ...next,
    title: popupNames(data.people, i18n),
    body: verb ? i18n.t(verb) : next.body,
    data,
    renotify: !shown.ids.includes(next.data.ids[0]),
  }
}
